import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Drama, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";

const HERO = "https://images.unsplash.com/photo-1646186213674-35cf3c0c398d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, firstName.trim(), lastName.trim());
      toast.success(mode === "login" ? "Welcome back" : "Your watchlist is ready");
      nav("/");
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <Seo title="Sign in" noindex />
      <div className="relative hidden overflow-hidden lg:block hero-scrim grain" style={{ "--hero-img": `url(${HERO})` }}>
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-2 text-amber-100">
            <Drama className="h-7 w-7 text-[#C89B3C]" />
            <span className="font-serif text-2xl font-semibold">My Theatre</span>
          </div>
          <div>
            <p className="overline-label text-[#C89B3C]">Your West End hub</p>
            <h1 className="mt-3 max-w-md font-serif text-5xl font-semibold leading-[1.05] text-amber-50">
              Everything West End, in one place.
            </h1>
            <p className="mt-4 max-w-sm text-amber-100/70">
              Follow your favourite shows and performers, get alerted the moment a cast changes, compare
              ticket prices, and play our daily theatre trivia.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <Drama className="h-6 w-6 text-primary" />
            <span className="font-serif text-xl font-semibold">My Theatre</span>
          </div>
          <p className="overline-label text-[hsl(var(--gold))]">{mode === "login" ? "Welcome back" : "Get started"}</p>
          <h2 className="mt-2 font-serif text-3xl font-semibold">
            {mode === "login" ? "Sign in" : "Create your account"}
          </h2>

          <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "register" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">First name</label>
                  <input
                    data-testid="auth-first-name-input"
                    required
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-input bg-card px-4 py-2.5 outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Last name</label>
                  <input
                    data-testid="auth-last-name-input"
                    required
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-input bg-card px-4 py-2.5 outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border border-input bg-card px-4 py-2.5 outline-none focus:ring-2 focus:ring-ring"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-xl border border-input bg-card px-4 py-2.5 outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
                />
              </div>
              {mode === "login" && (
                <div className="-mt-2 text-right">
                  <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-sm text-primary hover:underline">
                    Forgotten your password?
                  </Link>
                </div>
              )}
              {err && <p data-testid="auth-error" className="text-sm text-destructive">{err}</p>}
            <button
              data-testid="auth-submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-colors hover:bg-[#B82332] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              className="font-semibold text-primary hover:underline"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
